import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons/faEllipsis';
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink';
import { faLinkSlash } from '@fortawesome/free-solid-svg-icons/faLinkSlash';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import styled from 'styled-components';

import { AtiraIcon } from '../../components/AtiraIcon';
import { Button } from '../../components/Button';
import { CopyButton } from '../../components/CopyButton';
import { Flex } from '../../components/Flex';
import { Text } from '../../components/Text';
import { TextWithTruncated } from '../../components/TextWithTruncated';
import { UpgradeButton } from '../../components/UpgradeButton';
import { urlTestIDs } from '../../constants/test/url.testIds';
import { GenericAvailabilityStatus } from '../../model/shared/types/GenericAvailabilityStatus.enum';
import { AtiraURL } from '../../model/url/AtiraURL';
import { FontSize } from '../../theme/FontSize';
import { standardDate } from '../../utils/Date';
import {
  Dropdown,
  MenuProps,
  TableProps,
  Tooltip,
} from '../../utils/local-antd';

const StyledActionButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.main} !important;
  font-size: ${FontSize.s};

  & > div {
    justify-content: flex-start !important;
  }
`;

const LockButton = styled(Button)`
  padding: 0;
  margin: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.danger};
`;

const StyledCopyButton = styled(CopyButton)`
  padding: 0;
`;

type FNType = ATVoidFunction<AtiraURL>;

type FieldFunctions = {
  disable: FNType;
  enable: FNType;
  delete: FNType;
  update: FNType;
  t: TFunction;
};

const getFieldWidth = (field: keyof AtiraURL | 'actions') => {
  switch (field) {
    case 'originalURL':
    case 'shortURL':
    case 'label':
      return 120;
    case 'actions':
      return 50;
    case 'visits':
    case 'enabled':
      return 80;
    case 'createdAt':
    default:
      return 150;
  }
};

const getFieldInfo = (
  column: keyof AtiraURL | 'actions',
  functions: FieldFunctions,
) => {
  const { t, enable, disable, delete: deleteURL, update } = functions;

  switch (column) {
    case 'actions':
      return {
        title: t('common.actions'),
        key: 'actions',
        render: (url: AtiraURL) => {
          const items: MenuProps['items'] = [
            {
              key: 'g1',
              type: 'group',
              children: [
                {
                  label: (
                    <StyledActionButton
                      onClick={() => enable(url)}
                      icon={faLink}
                    >
                      {t('common.enable')}
                    </StyledActionButton>
                  ),
                  valid:
                    url.enabled === false &&
                    url.status === GenericAvailabilityStatus.AVAILABLE,
                },
                {
                  label: (
                    <StyledActionButton
                      onClick={() => update(url)}
                      icon={faPen}
                    >
                      {t('common.edit')}
                    </StyledActionButton>
                  ),
                },
                {
                  label: (
                    <StyledActionButton
                      onClick={() => disable(url)}
                      icon={faLinkSlash}
                    >
                      {t('common.disable')}
                    </StyledActionButton>
                  ),
                  valid:
                    url.enabled === true &&
                    url.status === GenericAvailabilityStatus.AVAILABLE,
                },
                {
                  label: (
                    <StyledActionButton
                      onClick={() => deleteURL(url)}
                      icon={faTrash}
                      dataTestId={urlTestIDs.list.table.delete(url.originalURL)}
                    >
                      {t('common.delete')}
                    </StyledActionButton>
                  ),
                },
              ]
                .filter((child) => child.valid !== false)
                .map((child, index) => ({
                  ...child,
                  key: index,
                  style: { padding: 4 },
                })),
            },
          ];

          return (
            <Flex justifyContent="center">
              <Dropdown
                menu={{ items }}
                placement="bottomLeft"
                arrow={{ pointAtCenter: true }}
                trigger={['click']}
                className={urlTestIDs.list.table.actions(url.originalURL!)}
              >
                <Tooltip title={t('common.actions')}>
                  <Button
                    icon={faEllipsis}
                    backgroundColor="transparent"
                    margin="0"
                    color="fgPrimary"
                  />
                </Tooltip>
              </Dropdown>
            </Flex>
          );
        },
        reorder: true,
        center: true,
        width: getFieldWidth('actions'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('actions') },
        }),
        data: { column, enabled: true },
        fixed: 'left',
      };

    case 'label':
      return {
        title: t('common.label'),
        key: 'label',
        render: (url: AtiraURL) => {
          return (
            <TextWithTruncated length={15}>{url.label || ''}</TextWithTruncated>
          );
        },
        sorter: (a: AtiraURL, b: AtiraURL) =>
          a?.label?.localeCompare(b?.label || ''),
        sortable: true,
        width: getFieldWidth('label'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('label') },
        }),
      };

    case 'originalURL':
      return {
        title: t('shorten_url.original_url'),
        key: 'originalURL',
        render: ({ originalURL }: AtiraURL) => {
          return (
            <Flex justifyContent="space-between" alignItems="center" gap="m">
              <TextWithTruncated length={30} textProps={{ direction: 'ltr' }}>
                {originalURL}
              </TextWithTruncated>
              <StyledCopyButton copy={originalURL} iconSize="xl" />
            </Flex>
          );
        },
        width: getFieldWidth('originalURL'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('originalURL') },
        }),
      };

    case 'shortURL':
      return {
        title: t('shorten_url.short_url'),
        key: 'shortURL',
        render: ({ shortURL, status }: AtiraURL) => {
          return (
            <Flex justifyContent="space-between" alignItems="center" gap="m">
              <TextWithTruncated length={25} textProps={{ direction: 'ltr' }}>
                {shortURL!}
              </TextWithTruncated>
              {status !== GenericAvailabilityStatus.RESTRICTED ? (
                <StyledCopyButton copy={shortURL!} iconSize="xl" />
              ) : null}
            </Flex>
          );
        },
        width: getFieldWidth('shortURL'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('shortURL') },
        }),
      };

    case 'createdAt':
      return {
        title: t('shorten_url.create_date'),
        key: 'time',
        render: ({ createdAt }: AtiraURL) => {
          return <Text>{standardDate(new Date(createdAt))}</Text>;
        },
        sorter: (a: AtiraURL, b: AtiraURL) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateA - dateB;
        },
        sortable: true,
        reorder: true,
        width: getFieldWidth('createdAt'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('createdAt') },
        }),
        data: { column, enabled: true },
        showSorterTooltip: false,
      };

    case 'visits':
      return {
        title: t('common.visits'),
        key: 'visits',
        render: ({ visits }: AtiraURL) => {
          return <Text>{visits}</Text>;
        },
        width: getFieldWidth('visits'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('visits') },
        }),
      };

    case 'enabled':
      return {
        title: t('common.enabled'),
        key: 'enabled',
        render: ({ enabled, status }: AtiraURL) => {
          if (status === GenericAvailabilityStatus.RESTRICTED) {
            return (
              <Tooltip
                title={
                  <Trans
                    i18nKey={'restricted.tooltip'}
                    components={[<UpgradeButton insideTooltip />]}
                  />
                }
              >
                <LockButton icon={faLock} />
              </Tooltip>
            );
          }

          return (
            <AtiraIcon
              icon={enabled ? faCheck : faXmark}
              color={enabled ? 'success' : 'danger'}
            />
          );
        },
        width: getFieldWidth('enabled'),
        onCell: () => ({
          style: { minWidth: getFieldWidth('enabled') },
        }),
      };

    default:
      return {
        title: column,
        key: column,
        render: (task: AtiraURL) => (
          <TextWithTruncated length={30}>
            {task?.[column]?.toString() || ''}
          </TextWithTruncated>
        ),
        sortable: true,
        reorder: true,
        data: { column, enabled: true },
        width: getFieldWidth(column),
        onCell: () => ({
          style: { minWidth: getFieldWidth(column) },
        }),
      };
  }
};

export const createUserURLsTableColumns = (
  fields: (keyof AtiraURL | 'actions')[],
  functions: FieldFunctions,
) => {
  return fields.map((field) =>
    getFieldInfo(field, functions),
  ) as TableProps<AtiraURL>['columns'];
};
